/**
 * Patient-level readings shared by the overview snapshot and the profile
 * modal, so the two cannot disagree about whether a plan is still running.
 */

export type PlanStatus = "Active" | "Expired" | "Not started" | "Paused";

const toDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** API sends PAUSE / pause / " Pause " — compare both sides lowercased. */
export const isPlanPaused = (data?: { plan_status?: string | null } | null) =>
  String(data?.plan_status ?? "")
    .trim()
    .toLowerCase() === "pause".toLowerCase();

/**
 * A paused plan wins over the date window: expiry can still be in the future
 * while the program is on hold. Otherwise derived from the plan dates. Null
 * when there is no usable expiry to judge by.
 */
export const getPlanStatus = (data: any): PlanStatus | null => {
  if (isPlanPaused(data)) return "Paused";

  const expiry = toDate(data?.plan_expiry_datetime || data?.expiry_date);
  if (!expiry) return null;

  const start = toDate(data?.plan_start_datetime);
  const now = new Date();

  if (start && start > now) return "Not started";
  return expiry > now ? "Active" : "Expired";
};

/** What the patient was enrolled for. */
export const getIndication = (data: any): string | null =>
  data?.medical_condition_name || data?.disease || null;

/**
 * Device names off the current plan's offerings — BCA scales, CGM sensors and
 * the like. Items are listed even when not taken up, so zero-quantity rows are
 * dropped.
 */
export const getPlanDevices = (data: any): string[] => {
  const offerings = data?.current_offering?.offerings;
  if (!Array.isArray(offerings)) return [];

  return offerings
    .filter((offering: any) => offering?.type === "Devices")
    .flatMap((offering: any) =>
      (offering?.items || [])
        .filter((item: any) => Number(item?.quantity) > 0)
        .map((item: any) => item?.meta_data?.device_name),
    )
    .filter(Boolean);
};
